import { imageWithinLimits, sniffInboundMedia } from './media-sniff.js';

/**
 * A campaign's picture is drawn at card size and at hero size on a page the
 * open internet loads, so the cap is what a photograph needs and no more. It is
 * well under the 7 MiB an inbound photograph may be, because nobody is sending
 * this one from a phone in the field.
 */
export const CAMPAIGN_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export type CampaignImageContentType = 'image/jpeg' | 'image/png';

export type SniffedCampaignImage =
  | {
      kind: 'image';
      contentType: CampaignImageContentType;
      extension: 'jpg' | 'png';
      width: number;
      height: number;
    }
  | { kind: 'unsupported' }
  | { kind: 'damaged' }
  | { kind: 'oversized' };

/**
 * The bytes decide, never the browser's content type or the file's extension.
 * A video, a HEIF, an AVIF or anything else is `unsupported`; a valid magic
 * number with an unreadable header is `damaged`; and an image past
 * `INBOUND_MEDIA_LIMITS` in either dimension is `oversized` — the same bounds
 * the inbound photograph reader applies, because the risk is the same decoder.
 */
export function sniffCampaignImage(bytes: Uint8Array): SniffedCampaignImage {
  const sniffed = sniffInboundMedia(bytes);
  if (sniffed.kind === 'damaged') return { kind: 'damaged' };
  if (sniffed.kind !== 'image') return { kind: 'unsupported' };
  if (!imageWithinLimits(sniffed)) return { kind: 'oversized' };
  return {
    kind: 'image',
    contentType: sniffed.contentType,
    extension: sniffed.extension,
    width: sniffed.width,
    height: sniffed.height,
  };
}
